// Zustand store —— 单一真相来源
//
// 三种数据写入路径：
// 1. 用户操作 (addTask/updateTask/...)
//    → 更新本地 state → persist 到 localStorage → enqueue outbox op → engine.schedulePush
// 2. 远端同步 pull
//    → _applyRemote(...) 把服务器 row 合并进来（tombstone 做删除、非空做 upsert）
//    → 不再 enqueue op（避免循环）
// 3. 初始化
//    → 若 localStorage 有持久化数据，直接使用；否则 fallback 到 mock-data（方便 demo）
//
// camelCase 字段是 UI 内部模型，同步边界上由 sync/mappers.ts 转 snake_case。

import {
  clampTagName,
  compareTasks,
  lexorankAfter,
  lexorankBetween,
  lexorankFirst,
  rescheduleToDate,
  uuidv7,
} from '@doneline/shared';
import { isNewer } from '@doneline/sync';
import type { PushOp } from '@doneline/sync';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  coalescedLocalStorage,
  flushPersistWrites,
  hasPersistWriteFailure,
} from './persist-storage';
import { getGuestTombstones } from './auth/guest-marker';
import { inRangeToday, isOverdue, isToday, next7Days, todayStr } from './date-utils';
import { advanceTaskDate, isValidRecurrence, recurrenceOccursOn } from './recurrence';
import {
  commentToSyncFields,
  projectToSyncFields,
  sectionToSyncFields,
  tagToSyncFields,
  taskToSyncFields,
} from './sync/mappers';
import {
  clearPendingTaskTags,
  getPendingTaskTags,
  outbox,
  setPendingTaskTags,
} from './sync/outbox';
import {
  clearRemoteTombstones,
  recordRemoteTombstones,
  recentRemoteTombstoneIds,
} from './sync/remote-tombstones';
import {
  mergeSnapshots,
  type CrossWindowEvidence,
  type PersistedEntitiesSlice,
} from './cross-window-merge';
import type { Comment, Priority, Project, Recurrence, Section, Tag, Task } from './types';

// ---------- type ----------

interface RemoteChanges {
  projects?: Array<{ row: Project; deletedAt: string | null }>;
  sections?: Array<{ row: Section; deletedAt: string | null }>;
  tasks?: Array<{ row: Task; deletedAt: string | null }>;
  comments?: Array<{ row: Comment; deletedAt: string | null }>;
  tags?: Array<{ row: Tag; deletedAt: string | null }>;
  taskTags?: Array<{ taskId: string; tagId: string; deletedAt: string | null }>;
}

interface State {
  projects: Project[];
  sections: Section[];
  tasks: Task[];
  tags: Tag[];
  comments: Comment[];

  // UI
  drawerTaskId: string | null;
  /**
   * Inline quick-edit bar (Todoist-style). Mutually exclusive with drawerTaskId.
   * Set by clicking the pencil icon on a task row.
   */
  editingTaskId: string | null;
  /**
   * Keyboard-navigated active row id. Driven by useTaskListKeyboard hook;
   * TaskItem renders a focus ring when its id matches. Not persisted.
   */
  activeRowTaskId: string | null;
  /**
   * Multi-selected task ids. Cmd/Ctrl+click toggles a row; Shift+click extends
   * a range from the last selected row. BulkActionBar appears whenever this is
   * non-empty. Not persisted — selection is intentionally tab-local.
   */
  selectedTaskIds: string[];
  /**
   * Anchor for shift-range select. Updated on every cmd-click; cleared along
   * with selection.
   */
  selectionAnchorId: string | null;
  quickCaptureOpen: boolean;
  searchOpen: boolean;
  helpOpen: boolean;
  /** ⌘K / Ctrl+K command palette overlay. */
  commandPaletteOpen: boolean;
  sidebarCollapsed: boolean;
  /**
   * 一览页行内元素显示偏好（标签 / 描述 / 子任务），默认全开。
   * 设备本地（persist，不走同步）：纯 UI 偏好，不新增非 push 写路径。
   */
  listDisplay: { tags: boolean; description: boolean; subtasks: boolean };

  // actions
  addTask: (input: Partial<Task> & { title: string }) => Task;
  updateTask: (id: string, patch: Partial<Task>) => void;
  // 返回值（R41-2）：勾选周期任务不是完成而是推进（行从 Today 直接消失）——调用方据此
  // toast 提示「已推进到 X / 重复已结束」，否则用户以为任务被误删。非周期路径返回 undefined。
  toggleTaskStatus: (
    id: string,
  ) => { advanced: boolean; nextDate: string | null; exhausted: boolean } | undefined;
  setTaskRecurrence: (id: string, rec: Recurrence | null) => void;
  deleteTask: (id: string) => void;
  addProject: (p: Omit<Project, 'id' | 'sortKey' | 'archivedAt' | 'description'>) => Project;
  updateProject: (id: string, patch: Partial<Project>) => void;
  deleteProject: (id: string) => void;
  reorderProjects: (ids: string[]) => void;
  addSection: (s: Omit<Section, 'id' | 'sortKey'> & { sortKey?: string }) => Section;
  updateSection: (id: string, patch: Partial<Section>) => void;
  deleteSection: (id: string) => void;
  reorderTasks: (
    bucket: { projectId: string | null; sectionId: string | null },
    ids: string[],
  ) => void;
  moveTaskToBucket: (
    taskId: string,
    dest: { projectId: string | null; sectionId: string | null; targetIndex: number },
  ) => void;
  reorderSections: (projectId: string, ids: string[]) => void;
  addTag: (t: Omit<Tag, 'id'>) => Tag;
  updateTag: (id: string, patch: Partial<Pick<Tag, 'name' | 'color'>>) => void;
  deleteTag: (id: string) => void;
  toggleTaskTag: (taskId: string, tagId: string) => void;
  setTaskTags: (taskId: string, tagIds: string[]) => void;
  addComment: (taskId: string, body: string) => void;

  openDrawer: (id: string | null) => void;
  setEditingTask: (id: string | null) => void;
  setActiveRow: (id: string | null) => void;
  /**
   * Toggle a row's selection.
   *  - 'toggle' (cmd/ctrl+click) flips the single id; updates anchor.
   *  - 'range'  (shift+click) selects from anchor → id within visibleIds.
   *  - 'replace' selects just this id.
   */
  selectTaskInList: (
    id: string,
    mode: 'toggle' | 'range' | 'replace',
    visibleIds: string[],
  ) => void;
  clearSelection: () => void;
  /** Bulk apply a patch to every selected task (NOT for completion — use bulkCompleteSelected). */
  bulkUpdateSelected: (patch: Partial<Task>) => void;
  /** Bulk complete: per-task toggleTaskStatus so completedAt is stamped + recurrence advances. */
  bulkCompleteSelected: () => void;
  /**
   * Todoist-style "Reschedule overdue": move every open overdue top-level task
   * (the ones shown in the Today › Overdue group) to `targetDate` (YYYY-MM-DD),
   * preserving each task's date shape (all-day / timed / range) and recurrence.
   * Returns how many tasks were moved (for the confirmation toast).
   */
  rescheduleOverdue: (targetDate: string) => number;
  /** Bulk delete every selected task (cascades to subtasks via deleteTask). */
  bulkDeleteSelected: () => void;
  // 批量核心（ids 显式传入；上面的 *Selected 是 selection wrapper，mobile BulkActionBar
  // 直接调同名 action）：单次 set() + op 收集后 enqueueMany 一次入队 + 单次 schedulePush。
  // 逐条走 toggleTaskStatus / updateTask / deleteTask 时每条都一次 outbox enqueue
  // （load + 全量扫描 + save）+ 一次 set()（selector 全表重算），全选数百条就是 O(N²)
  // 写放大、主线程冻结（guardrail E 的批量入队反模式）。语义与逐条完全一致，见实现注释。
  bulkCompleteTasks: (ids: string[]) => void;
  bulkPatchTasks: (ids: string[], patch: Partial<Task>) => void;
  bulkDeleteTasks: (ids: string[]) => void;
  setQuickCaptureOpen: (v: boolean) => void;
  setSearchOpen: (v: boolean) => void;
  setHelpOpen: (v: boolean) => void;
  setCommandPaletteOpen: (v: boolean) => void;
  toggleSidebar: () => void;
  setListDisplay: (patch: Partial<State['listDisplay']>) => void;

  // 持久化的「数据归属用户」。共享设备换账号时，用它判断本地数据是不是当前用户的，
  // 不是就先清空再 bootstrap，杜绝跨租户读（看到别人数据）与跨租户写（把别人 outbox 推到自己账户）。
  ownerUserId: string | null;

  // 本地数据代际：guest「放弃本地数据」（discard）时 +1。跨窗口合并判据的一部分 ——
  // guest 态 owner 恒 null，仅靠 owner 相等判断时另一窗口的陈旧内存快照会经并集把
  // discard 撤销（数据复活并在登录后上行）。代际不同 → 整包采纳盘上，不并集。
  dataEpoch: number;
  bumpDataEpoch: () => void;

  // sync internal (engine 使用)
  _applyRemote: (changes: RemoteChanges) => void;
  _hasAnyData: () => boolean;
  // 仅声明归属（首次登录，不清数据）
  _setOwner: (userId: string) => void;
  // 切换用户时清空本地实体 + outbox，并把归属设为新用户
  clearLocalData: (newOwnerId: string | null) => void;

  // import/export。persisted=false 表示本地存储写入失败（QuotaExceeded 等）——
  // 数据只在内存里，刷新即丢，UI 必须弹错误而非成功（否则「导入成功」是假象）。
  importPayload: (p: ImportPayloadInput) => { merged: number; persisted: boolean };
}

interface ImportPayloadInput {
  projects?: Project[];
  sections?: Section[];
  tasks?: Task[];
  tags?: Tag[];
  comments?: Comment[];
}

// ---------- helpers ----------

const now = () => new Date().toISOString();
const newId = () => uuidv7();

// --- LexoRank helpers ---
function rankAppendFor(sortKeys: string[]): string {
  if (sortKeys.length === 0) return lexorankFirst();
  const sorted = [...sortKeys].sort();
  return lexorankAfter(sorted[sorted.length - 1]!);
}

// 给 N 个 id 生成均匀分布的 LexoRank 序列
function rankSequence(n: number): string[] {
  const keys: string[] = [];
  let prev: string | null = null;
  for (let i = 0; i < n; i++) {
    const k = lexorankBetween(prev, null);
    keys.push(k);
    prev = k;
  }
  return keys;
}

// 用动态 import() 避免循环依赖（engine 里 import store）。
// 测试环境下 vi.resetModules + 并发 import 偶发 partial module → 容错处理。
function schedulePushSafely() {
  void import('./sync/engine')
    .then((m) => {
      if (typeof m.schedulePush === 'function') m.schedulePush();
    })
    .catch(() => {
      // ignore: engine 加载失败 (e.g. 测试环境无 env)
    });
}

// 只构造 op 不入队：批量路径（importPayload）先收集成数组再一次 enqueueMany，
// 避免逐条 enqueue 的 O(N²) 同步 localStorage 写（每条都 load+全量扫描+save）。
function makeUpsertOp(
  kind: PushOp['entity_kind'],
  entityId: string,
  fields: Record<string, unknown>,
): PushOp {
  return {
    op_id: uuidv7(),
    entity_kind: kind,
    entity_id: entityId,
    op_kind: 'upsert',
    base_row_version: null,
    client_ts: now(),
    fields,
  };
}

// makeUpsertOp 的 delete 版：批量路径（bulkDeleteTasks 等）先收集成数组再一次
// enqueueMany（理由同上）。clientTs 可传入让同一批级联 tombstone 共享一个时钟。
function makeDeleteOp(kind: PushOp['entity_kind'], entityId: string, clientTs?: string): PushOp {
  return {
    op_id: uuidv7(),
    entity_kind: kind,
    entity_id: entityId,
    op_kind: 'delete',
    base_row_version: null,
    client_ts: clientTs ?? now(),
    fields: {},
  };
}

function enqueueUpsert(
  kind: PushOp['entity_kind'],
  entityId: string,
  fields: Record<string, unknown>,
) {
  outbox.enqueue(makeUpsertOp(kind, entityId, fields));
  schedulePushSafely();
}

function enqueueDelete(kind: PushOp['entity_kind'], entityId: string) {
  const op: PushOp = {
    op_id: uuidv7(),
    entity_kind: kind,
    entity_id: entityId,
    op_kind: 'delete',
    base_row_version: null,
    client_ts: now(),
    fields: {},
  };
  outbox.enqueue(op);
  schedulePushSafely();
}

// ---------- store ----------

// 跨窗口快照合并的证据收集（写侧 RMW 与 storage 事件 rehydrate 共用一套；
// 语义见 lib/cross-window-merge.ts 文件头）。
export function gatherCrossWindowEvidence(): CrossWindowEvidence {
  const { pendingUpsertIds, pendingDeleteIds } = outbox.pendingEntityEvidence();
  // guest 期删除证据（设计 v4 §3.5 + v4.1-3）：guest 不写 outbox，删除证据走
  // guestTombstones。**只在 owner==null（guest/未采纳）时消费** —— authed 态下被注入的
  // 条目不得隐藏用户数据；task_tag 与非法形状条目丢弃（与 pendingEntityEvidence 同语义）。
  if (useStore.getState().ownerUserId == null) {
    for (const t of getGuestTombstones()) {
      if (t.kind === 'task_tag') continue;
      pendingDeleteIds.add(t.id);
      pendingUpsertIds.delete(t.id);
    }
  }
  return { pendingUpsertIds, pendingDeleteIds, remoteTombstoneIds: recentRemoteTombstoneIds() };
}

// persist partialize 的形状（storage 泛型 + 跨窗口合并共用）。
type PersistedShape = PersistedEntitiesSlice & {
  ownerUserId: string | null;
  dataEpoch?: number;
  listDisplay: State['listDisplay'];
};

function toEntitiesSlice(s: {
  projects?: Project[];
  sections?: Section[];
  tasks?: Task[];
  tags?: Tag[];
  comments?: Comment[];
}): PersistedEntitiesSlice {
  return {
    projects: s.projects ?? [],
    sections: s.sections ?? [],
    tasks: s.tasks ?? [],
    tags: s.tags ?? [],
    comments: s.comments ?? [],
  };
}

export const useStore = create<State>()(
  persist(
    (set, get) => ({
      projects: [],
      sections: [],
      tasks: [],
      tags: [],
      comments: [],
      ownerUserId: null,
      dataEpoch: 0,

      drawerTaskId: null,
      editingTaskId: null,
      activeRowTaskId: null,
      selectedTaskIds: [],
      selectionAnchorId: null,
      quickCaptureOpen: false,
      searchOpen: false,
      helpOpen: false,
      commandPaletteOpen: false,
      sidebarCollapsed: false,
      listDisplay: { tags: true, description: true, subtasks: true },

      addTask: (input) => {
        const bucketProject = input.projectId ?? null;
        const bucketSection = input.sectionId ?? null;
        const bucketParent = input.parentTaskId ?? null;
        // 子任务（有 parentTaskId）的 sortKey 要在同一父任务的子任务中排，
        // 不能拿顶层 (null,null) bucket 来排，否则 selectChildrenOf 排序会错位。
        const siblings = bucketParent
          ? get().tasks.filter((t) => t.parentTaskId === bucketParent && t.status === 0)
          : get().tasks.filter(
              (t) =>
                t.projectId === bucketProject &&
                t.sectionId === bucketSection &&
                t.status === 0 &&
                !t.parentTaskId,
            );
        const sortKey = rankAppendFor(siblings.map((t) => t.sortKey));

        const task: Task = {
          id: newId(),
          projectId: null,
          sectionId: null,
          parentTaskId: null,
          description: null,
          priority: 0,
          status: 0,
          dueDate: null,
          dueAt: null,
          startDate: null,
          endDate: null,
          completedAt: null,
          recurrence: null,
          sortKey,
          createdAt: now(),
          updatedAt: now(),
          tagIds: [],
          ...input,
        };
        set((s) => ({ tasks: [...s.tasks, task] }));
        enqueueUpsert('task', task.id, taskToSyncFields(task));

        // enqueue task_tag ops for initial tagIds
        if (task.tagIds && task.tagIds.length > 0) {
          for (const tagId of task.tagIds) {
            outbox.enqueue({
              op_id: uuidv7(),
              entity_kind: 'task_tag',
              entity_id: `${task.id}:${tagId}`,
              op_kind: 'upsert',
              base_row_version: null,
              client_ts: now(),
              fields: {},
            });
          }
          schedulePushSafely();
        }

        return task;
      },

      updateTask: (id, patch) => {
        // 如果 patch 含 tagIds，diff 并走 task_tag 独立 op（task 表没有 tagIds 字段）
        if ('tagIds' in patch) {
          const prev = get().tasks.find((t) => t.id === id)?.tagIds ?? [];
          const next = patch.tagIds ?? [];
          const prevSet = new Set(prev);
          const nextSet = new Set(next);
          for (const tagId of next) {
            if (!prevSet.has(tagId)) {
              outbox.enqueue({
                op_id: uuidv7(),
                entity_kind: 'task_tag',
                entity_id: `${id}:${tagId}`,
                op_kind: 'upsert',
                base_row_version: null,
                client_ts: now(),
                fields: {},
              });
            }
          }
          for (const tagId of prev) {
            if (!nextSet.has(tagId)) {
              outbox.enqueue({
                op_id: uuidv7(),
                entity_kind: 'task_tag',
                entity_id: `${id}:${tagId}`,
                op_kind: 'delete',
                base_row_version: null,
                client_ts: now(),
                fields: {},
              });
            }
          }
          schedulePushSafely();
        }

        // 只有真的动了 task 表字段才 bump updatedAt：tagIds-only patch 不产生 task op
        // （服务端 task.updated_at 不前移），本地却 bump = 幻影时钟——之后 pull 回来的
        // 并发远端编辑（合法更新的 title 等）会被 mergeList 的 LWW 判旧而**永久丢弃**
        // （游标已推进，行不会重发）。tagIds 本身由 task_tag 同步路径保护，不需要行时钟。
        const taskFields = taskToSyncFields(patch);
        const bump = Object.keys(taskFields).length > 0;
        set((s) => ({
          tasks: s.tasks.map((t) =>
            t.id === id ? { ...t, ...patch, ...(bump ? { updatedAt: now() } : {}) } : t,
          ),
        }));
        if (bump) {
          enqueueUpsert('task', id, taskFields);
        }
      },

      toggleTaskStatus: (id) => {
        const t = get().tasks.find((x) => x.id === id);
        if (!t) return;
        const isDone = t.status === 1;

        // Todoist 风格：勾选一个尚未完成的 recurring task → 推进 dueDate，保持 status=0
        if (!isDone && t.recurrence && isValidRecurrence(t.recurrence)) {
          const adv = advanceTaskDate({ dueDate: t.dueDate, dueAt: t.dueAt }, t.recurrence);
          if (adv.exhausted) {
            // endDate 用尽：当作真正完成
            const patch: Partial<Task> = {
              status: 1,
              completedAt: now(),
              recurrence: null,
            };
            set((s) => ({
              tasks: s.tasks.map((x) => (x.id === id ? { ...x, ...patch, updatedAt: now() } : x)),
            }));
            enqueueUpsert('task', id, taskToSyncFields(patch));
            return { advanced: false, nextDate: null, exhausted: true };
          }
          const patch: Partial<Task> = {
            dueDate: adv.dueDate,
            dueAt: adv.dueAt,
            // 区间任务不会进入这里（advanceTaskDate 不返回 startDate/endDate），
            // 但保险起见清掉残留以维持 task_date_shape CHECK
            startDate: null,
            endDate: null,
            // 推进后 status 仍为 0（未完成），必须清掉可能残留的 completedAt，
            // 否则会出现 status=0 却带 completedAt 的不一致状态
            completedAt: null,
          };
          set((s) => ({
            tasks: s.tasks.map((x) => (x.id === id ? { ...x, ...patch, updatedAt: now() } : x)),
          }));
          enqueueUpsert('task', id, taskToSyncFields(patch));
          return {
            advanced: true,
            nextDate: adv.dueDate ?? (adv.dueAt ? adv.dueAt.slice(0, 10) : null),
            exhausted: false,
          };
        }

        const patch: Partial<Task> = {
          status: isDone ? 0 : 1,
          completedAt: isDone ? null : now(),
        };
        set((s) => ({
          tasks: s.tasks.map((x) => (x.id === id ? { ...x, ...patch, updatedAt: now() } : x)),
        }));
        enqueueUpsert('task', id, taskToSyncFields(patch));
      },

      setTaskRecurrence: (id, rec) => {
        const t = get().tasks.find((x) => x.id === id);
        if (!t) return;
        const patch: Partial<Task> = { recurrence: rec };
        set((s) => ({
          tasks: s.tasks.map((x) => (x.id === id ? { ...x, ...patch, updatedAt: now() } : x)),
        }));
        enqueueUpsert('task', id, taskToSyncFields(patch));
      },

      deleteTask: (id) => {
        const target = get().tasks.find((t) => t.id === id);
        const children = get().tasks.filter((t) => t.parentTaskId === id);
        // 服务端无跨表 FK 级联、GC 只回收 deleted_at 非空的行：task 删掉后它（含子任务）的
        // comment / task_tag 若不发 tombstone，会在本地与服务端无界累积永不 GC；新设备
        // bootstrap 拉到孤儿 task_tag 还会永久滞留 pendingTaskTags 缓冲。级联范式与 deleteTag 对齐。
        const deletedIds = new Set([id, ...children.map((c) => c.id)]);
        const orphanComments = get().comments.filter((c) => deletedIds.has(c.taskId));
        set((s) => ({
          tasks: s.tasks.filter((t) => t.id !== id && t.parentTaskId !== id),
          comments: s.comments.filter((c) => !deletedIds.has(c.taskId)),
          drawerTaskId: s.drawerTaskId === id ? null : s.drawerTaskId,
          editingTaskId: s.editingTaskId === id ? null : s.editingTaskId,
          selectedTaskIds: s.selectedTaskIds.includes(id)
            ? s.selectedTaskIds.filter((x) => x !== id)
            : s.selectedTaskIds,
        }));
        enqueueDelete('task', id);
        for (const c of children) enqueueDelete('task', c.id);
        for (const c of orphanComments) enqueueDelete('comment', c.id);
        for (const t of [target, ...children]) {
          if (!t) continue;
          for (const tagId of t.tagIds ?? []) enqueueDelete('task_tag', `${t.id}:${tagId}`);
        }
      },

      addProject: (p) => {
        const sortKey = rankAppendFor(get().projects.map((x) => x.sortKey));
        const proj: Project = {
          ...p,
          id: newId(),
          sortKey,
          archivedAt: null,
          description: null,
          updatedAt: now(),
        };
        set((s) => ({ projects: [...s.projects, proj] }));
        enqueueUpsert('project', proj.id, projectToSyncFields(proj));
        return proj;
      },

      updateProject: (id, patch) => {
        const patchWithTime = { ...patch, updatedAt: now() };
        set((s) => ({
          projects: s.projects.map((p) => (p.id === id ? { ...p, ...patchWithTime } : p)),
        }));
        enqueueUpsert('project', id, projectToSyncFields(patch));
      },

      deleteProject: (id) => {
        const affectedTasks = get().tasks.filter((t) => t.projectId === id);
        // 项目下的分区是 project-scoped 的：项目删掉后这些 section 就成了孤儿，
        // 既无处展示也永远不会被 GC。必须一并从本地 state 移除并下发 tombstone，
        // 否则它们会一直滞留在 store / 服务端。
        const affectedSections = get().sections.filter((s) => s.projectId === id);
        set((s) => ({
          projects: s.projects.filter((p) => p.id !== id),
          sections: s.sections.filter((sec) => sec.projectId !== id),
          tasks: s.tasks.map((t) =>
            // updatedAt 必须前移：否则本地"移出项目"的 updatedAt 停在旧值，push 落地前一次
            // 携旧 project_id 的 pull 会因 LWW 非严格更新而覆盖回去（"移出又弹回"竞态）。
            t.projectId === id ? { ...t, projectId: null, sectionId: null, updatedAt: now() } : t,
          ),
        }));
        enqueueDelete('project', id);
        for (const sec of affectedSections) {
          enqueueDelete('section', sec.id);
        }
        // 把被影响的 task 上报 project_id=null
        for (const t of affectedTasks) {
          enqueueUpsert('task', t.id, { project_id: null, section_id: null });
        }
      },

      reorderProjects: (ids) => {
        const keys = rankSequence(ids.length);
        const timeStamp = now();
        set((s) => {
          const map = new Map(s.projects.map((p) => [p.id, p]));
          const reordered: Project[] = ids
            .map((id, idx) => {
              const p = map.get(id);
              if (!p) return null;
              return { ...p, sortKey: keys[idx]!, updatedAt: timeStamp };
            })
            .filter((p): p is NonNullable<typeof p> => p !== null);
          const idSet = new Set(ids);
          const untouched = s.projects.filter((p) => !idSet.has(p.id));
          return { projects: [...reordered, ...untouched] };
        });
        const updated = get().projects;
        for (const p of updated) {
          if (ids.includes(p.id)) {
            enqueueUpsert('project', p.id, { sort_key: p.sortKey });
          }
        }
      },

      addSection: (s) => {
        const siblings = get().sections.filter((x) => x.projectId === s.projectId);
        const sortKey = s.sortKey ?? rankAppendFor(siblings.map((x) => x.sortKey));
        const sec: Section = {
          id: newId(),
          ...s,
          sortKey,
          updatedAt: now(),
        };
        set((st) => ({ sections: [...st.sections, sec] }));
        enqueueUpsert('section', sec.id, sectionToSyncFields(sec));
        return sec;
      },

      updateSection: (id, patch) => {
        const patchWithTime = { ...patch, updatedAt: now() };
        set((st) => ({
          sections: st.sections.map((s) => (s.id === id ? { ...s, ...patchWithTime } : s)),
        }));
        enqueueUpsert('section', id, sectionToSyncFields(patch));
      },

      deleteSection: (id) => {
        // section 无 FK 级联：本地把任务 sectionId 清空后，必须同时把 section_id=null 上行，
        // 否则服务器仍存旧 section_id，换设备/重 bootstrap 时本地的"移出分区"会丢失（与
        // deleteProject 对齐）。
        const affectedTasks = get().tasks.filter((t) => t.sectionId === id);
        set((st) => ({
          sections: st.sections.filter((s) => s.id !== id),
          tasks: st.tasks.map((t) =>
            // updatedAt 前移，同 deleteProject：防 push 落地前的旧 pull 把 section_id 覆盖回去。
            t.sectionId === id ? { ...t, sectionId: null, updatedAt: now() } : t,
          ),
        }));
        enqueueDelete('section', id);
        for (const t of affectedTasks) enqueueUpsert('task', t.id, { section_id: null });
      },

      reorderTasks: ({ projectId, sectionId }, ids) => {
        const keys = rankSequence(ids.length);
        const idSet = new Set(ids);
        set((s) => ({
          tasks: s.tasks.map((t) => {
            if (!idSet.has(t.id)) return t;
            const idx = ids.indexOf(t.id);
            return {
              ...t,
              projectId,
              sectionId,
              sortKey: keys[idx]!,
              updatedAt: now(),
            };
          }),
        }));
        const updated = get().tasks.filter((t) => idSet.has(t.id));
        for (const t of updated) {
          enqueueUpsert('task', t.id, {
            sort_key: t.sortKey,
            project_id: t.projectId,
            section_id: t.sectionId,
          });
        }
      },

      moveTaskToBucket: (taskId, { projectId, sectionId, targetIndex }) => {
        const task = get().tasks.find((t) => t.id === taskId);
        if (!task) return;
        const siblings = get()
          .tasks.filter(
            (t) =>
              t.id !== taskId &&
              t.projectId === projectId &&
              t.sectionId === sectionId &&
              t.status === 0 &&
              !t.parentTaskId,
          )
          .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
        const before = targetIndex > 0 ? (siblings[targetIndex - 1]?.sortKey ?? null) : null;
        const after =
          targetIndex < siblings.length ? (siblings[targetIndex]?.sortKey ?? null) : null;
        let newKey: string;
        // 间隙耗尽时 lexorankBetween 会抛（"caller should rebalance"）。离线大量同位插入会触发。
        // 客户端没有别的 rebalance 入口（只有 batch job，sync 关时不跑）→ 这里兜底整桶重排，
        // 否则未捕获异常会从 DnD handler 冒出来（WKWebView 下表现为"拖不动/点不动"）。
        let rebalanced: { id: string; sortKey: string }[] | null = null;
        try {
          newKey = lexorankBetween(before, after);
        } catch {
          const ordered = [...siblings];
          ordered.splice(targetIndex, 0, task);
          const keys = rankSequence(ordered.length);
          rebalanced = ordered.map((tk, i) => ({ id: tk.id, sortKey: keys[i]! }));
          newKey = rebalanced.find((r) => r.id === taskId)!.sortKey;
        }
        const rebalanceMap = new Map(rebalanced?.map((r) => [r.id, r.sortKey]));
        set((s) => ({
          tasks: s.tasks.map((t) => {
            if (t.id === taskId) {
              return { ...t, projectId, sectionId, sortKey: newKey, updatedAt: now() };
            }
            const rk = rebalanceMap.get(t.id);
            return rk ? { ...t, sortKey: rk, updatedAt: now() } : t;
          }),
        }));
        enqueueUpsert('task', taskId, {
          sort_key: newKey,
          project_id: projectId,
          section_id: sectionId,
        });
        // 重排时其余兄弟的新 key 也要上行。
        if (rebalanced) {
          for (const r of rebalanced) {
            if (r.id !== taskId) enqueueUpsert('task', r.id, { sort_key: r.sortKey });
          }
        }
      },

      reorderSections: (projectId, ids) => {
        const keys = rankSequence(ids.length);
        const idSet = new Set(ids);
        const timeStamp = now();
        set((s) => ({
          sections: s.sections.map((sec) => {
            if (sec.projectId !== projectId || !idSet.has(sec.id)) return sec;
            const idx = ids.indexOf(sec.id);
            return { ...sec, sortKey: keys[idx]!, updatedAt: timeStamp };
          }),
        }));
        const updated = get().sections.filter((s) => idSet.has(s.id));
        for (const s of updated) {
          enqueueUpsert('section', s.id, { sort_key: s.sortKey });
        }
      },

      addTag: (t) => {
        // 名称统一 clamp（trim + 80 截断）——护栏 A：超长名 push 会被 shared schema 拒 = 静默丢失
        const tag: Tag = { id: newId(), ...t, name: clampTagName(t.name), updatedAt: now() };
        set((s) => ({ tags: [...s.tags, tag] }));
        enqueueUpsert('tag', tag.id, { name: tag.name, color: tag.color });
        return tag;
      },

      updateTag: (id, patch) => {
        // clamp 后为空（纯空白输入）→ 丢弃 name 字段：schema min(1)，空名 push 必拒 = 静默丢编辑
        let clean = patch;
        if (patch.name != null) {
          const name = clampTagName(patch.name);
          if (name) {
            clean = { ...patch, name };
          } else {
            const { name: _drop, ...rest } = patch;
            clean = rest;
          }
        }
        if (Object.keys(clean).length === 0) return;
        // 戳 updatedAt 让 mergeList 的 LWW 守卫生效（同 updateProject：漏 bump 会被
        // push 落地前的旧 pull 回滚——"改了名又弹回"）。
        set((s) => ({
          tags: s.tags.map((t) => (t.id === id ? { ...t, ...clean, updatedAt: now() } : t)),
        }));
        enqueueUpsert('tag', id, tagToSyncFields(clean));
      },

      deleteTag: (id) => {
        // 在删 tag 前先找出它挂在哪些 task 上，发送 task_tag 删除 op。
        // 批量必须走 enqueueMany（一次 load/save）：逐条 enqueue 是 O(K²) 序列化 +
        // K 次同步存储写，K=带该标签的任务数（含已完成，重度用户可达数百）会冻结
        // 主线程（guardrail E 的批量入队反模式）。服务端另有权威级联兜跨端未知关联。
        const affected = get().tasks.filter((t) => (t.tagIds ?? []).includes(id));
        set((s) => ({
          tags: s.tags.filter((t) => t.id !== id),
          tasks: s.tasks.map((t) =>
            (t.tagIds ?? []).includes(id)
              ? { ...t, tagIds: (t.tagIds ?? []).filter((x) => x !== id) }
              : t,
          ),
        }));
        const ts = now();
        outbox.enqueueMany([
          ...affected.map(
            (t): PushOp => ({
              op_id: uuidv7(),
              entity_kind: 'task_tag',
              entity_id: `${t.id}:${id}`,
              op_kind: 'delete',
              base_row_version: null,
              client_ts: ts,
              fields: {},
            }),
          ),
          {
            op_id: uuidv7(),
            entity_kind: 'tag',
            entity_id: id,
            op_kind: 'delete',
            base_row_version: null,
            client_ts: ts,
            fields: {},
          },
        ]);
        schedulePushSafely();
      },

      toggleTaskTag: (taskId, tagId) => {
        const task = get().tasks.find((t) => t.id === taskId);
        if (!task) return;
        const cur = task.tagIds ?? [];
        const on = cur.includes(tagId);
        const next = on ? cur.filter((x) => x !== tagId) : [...cur, tagId];
        get().updateTask(taskId, { tagIds: next });
      },

      setTaskTags: (taskId, tagIds) => {
        get().updateTask(taskId, { tagIds });
      },

      addComment: (taskId, body) => {
        const c: Comment = { id: newId(), taskId, body, createdAt: now(), updatedAt: now() };
        set((s) => ({ comments: [...s.comments, c] }));
        enqueueUpsert('comment', c.id, commentToSyncFields({ taskId, body }));
      },

      openDrawer: (id) =>
        set((s) => ({
          drawerTaskId: id,
          // Drawer and inline edit are mutually exclusive.
          editingTaskId: id ? null : s.editingTaskId,
        })),
      setEditingTask: (id) =>
        set((s) => ({
          editingTaskId: id,
          drawerTaskId: id ? null : s.drawerTaskId,
        })),
      setActiveRow: (id) => set({ activeRowTaskId: id }),

      selectTaskInList: (id, mode, visibleIds) => {
        set((s) => {
          if (mode === 'replace') {
            return { selectedTaskIds: [id], selectionAnchorId: id };
          }
          if (mode === 'toggle') {
            const has = s.selectedTaskIds.includes(id);
            return {
              selectedTaskIds: has
                ? s.selectedTaskIds.filter((x) => x !== id)
                : [...s.selectedTaskIds, id],
              selectionAnchorId: id,
            };
          }
          // 'range': from anchor → id within visibleIds; falls back to single
          // toggle if there's no anchor or either id is offscreen.
          const anchor = s.selectionAnchorId;
          const aIdx = anchor ? visibleIds.indexOf(anchor) : -1;
          const bIdx = visibleIds.indexOf(id);
          if (aIdx < 0 || bIdx < 0) {
            return {
              selectedTaskIds: [id],
              selectionAnchorId: id,
            };
          }
          const [lo, hi] = aIdx < bIdx ? [aIdx, bIdx] : [bIdx, aIdx];
          const range = visibleIds.slice(lo, hi + 1);
          // Union with existing selection so range-extend over previous picks
          // doesn't drop them.
          const next = Array.from(new Set([...s.selectedTaskIds, ...range]));
          return { selectedTaskIds: next, selectionAnchorId: id };
        });
      },

      clearSelection: () => set({ selectedTaskIds: [], selectionAnchorId: null }),

      bulkUpdateSelected: (patch) => {
        get().bulkPatchTasks(get().selectedTaskIds, patch);
      },

      // 批量完成必须走完成语义（而非 bulkUpdateSelected({status:1})）：否则只改 status，
      // 不写 completedAt、不推进 recurrence → 任务从「今日已完成」/周报统计里消失，recurring 任务也不滚动。
      bulkCompleteSelected: () => {
        get().bulkCompleteTasks(get().selectedTaskIds);
      },

      bulkDeleteSelected: () => {
        // 选中项全部被删 → 单次 set 里 selection 过滤后自然为空、anchor 一并清掉，
        // 不需要第二次 set（保持批量路径「恰一次 set」的护栏语义）。
        get().bulkDeleteTasks(get().selectedTaskIds);
      },

      // ---------- 批量核心（镜像 rescheduleOverdue / importPayload 范式） ----------

      // 批量完成。只完成「尚未完成」的（toggleTaskStatus 是开关语义，无条件套用会把选中里
      // 已完成的反勾回未完成、清掉 completedAt）。每任务与 toggleTaskStatus 的完成分支逐项
      // 一致：recurring 推进日期（endDate 用尽则真完成并清 recurrence），非 recurring 置
      // status=1 + completedAt；统一 bump updatedAt（LWW，guardrail A）。
      bulkCompleteTasks: (ids) => {
        const stamp = now();
        const byId = new Map(get().tasks.map((t) => [t.id, t]));
        const patches = new Map<string, Partial<Task>>();
        for (const id of ids) {
          const t = byId.get(id);
          if (!t || t.status === 1 || patches.has(id)) continue;
          if (t.recurrence && isValidRecurrence(t.recurrence)) {
            const adv = advanceTaskDate({ dueDate: t.dueDate, dueAt: t.dueAt }, t.recurrence);
            patches.set(
              id,
              adv.exhausted
                ? { status: 1, completedAt: stamp, recurrence: null }
                : {
                    dueDate: adv.dueDate,
                    dueAt: adv.dueAt,
                    // 同 toggleTaskStatus：清残留区间维持 date-shape CHECK；推进后仍未完成，
                    // 清 completedAt 防 status=0 却带 completedAt 的不一致状态。
                    startDate: null,
                    endDate: null,
                    completedAt: null,
                  },
            );
          } else {
            patches.set(id, { status: 1, completedAt: stamp });
          }
        }
        if (patches.size === 0) return;
        set((s) => ({
          tasks: s.tasks.map((t) => {
            const patch = patches.get(t.id);
            return patch ? { ...t, ...patch, updatedAt: stamp } : t;
          }),
        }));
        outbox.enqueueMany(
          Array.from(patches, ([id, patch]) => makeUpsertOp('task', id, taskToSyncFields(patch))),
        );
        schedulePushSafely();
      },

      // 批量版 updateTask：同一 patch 应用到全部 ids（多选的改期 / 移动 / 优先级）。
      // 语义逐项对齐 updateTask —— tagIds diff 走 task_tag 独立 op（task 表没有该字段）；
      // 只有真的动了 task 表字段才 bump updatedAt（tagIds-only patch 不产生 task op，
      // bump = 幻影时钟，见 updateTask 注释）。
      bulkPatchTasks: (ids, patch) => {
        const uniq = Array.from(new Set(ids));
        if (uniq.length === 0) return;
        const ops: PushOp[] = [];
        if ('tagIds' in patch) {
          const byId = new Map(get().tasks.map((t) => [t.id, t]));
          const next = patch.tagIds ?? [];
          const nextSet = new Set(next);
          for (const id of uniq) {
            const prev = byId.get(id)?.tagIds ?? [];
            const prevSet = new Set(prev);
            for (const tagId of next) {
              if (!prevSet.has(tagId)) ops.push(makeUpsertOp('task_tag', `${id}:${tagId}`, {}));
            }
            for (const tagId of prev) {
              if (!nextSet.has(tagId)) ops.push(makeDeleteOp('task_tag', `${id}:${tagId}`));
            }
          }
        }
        const taskFields = taskToSyncFields(patch);
        const bump = Object.keys(taskFields).length > 0;
        const stamp = now();
        const idSet = new Set(uniq);
        set((s) => ({
          tasks: s.tasks.map((t) =>
            idSet.has(t.id) ? { ...t, ...patch, ...(bump ? { updatedAt: stamp } : {}) } : t,
          ),
        }));
        if (bump) {
          for (const id of uniq) ops.push(makeUpsertOp('task', id, taskFields));
        }
        if (ops.length > 0) {
          outbox.enqueueMany(ops);
          schedulePushSafely();
        }
      },

      // 批量版 deleteTask：级联范围逐项对齐（直接子任务 + 两者的 comment / task_tag
      // tombstone，为什么必须级联见 deleteTask 注释），全部收进同一批 enqueueMany。
      bulkDeleteTasks: (ids) => {
        if (ids.length === 0) return;
        const roots = new Set(ids);
        const all = get().tasks;
        const deletedIds = new Set<string>(roots);
        for (const t of all) {
          if (t.parentTaskId && roots.has(t.parentTaskId)) deletedIds.add(t.id);
        }
        const deletedTasks = all.filter((t) => deletedIds.has(t.id));
        const orphanComments = get().comments.filter((c) => deletedIds.has(c.taskId));
        set((s) => ({
          tasks: s.tasks.filter((t) => !deletedIds.has(t.id)),
          comments: s.comments.filter((c) => !deletedIds.has(c.taskId)),
          drawerTaskId: s.drawerTaskId && deletedIds.has(s.drawerTaskId) ? null : s.drawerTaskId,
          editingTaskId:
            s.editingTaskId && deletedIds.has(s.editingTaskId) ? null : s.editingTaskId,
          selectedTaskIds: s.selectedTaskIds.some((x) => deletedIds.has(x))
            ? s.selectedTaskIds.filter((x) => !deletedIds.has(x))
            : s.selectedTaskIds,
          selectionAnchorId:
            s.selectionAnchorId && deletedIds.has(s.selectionAnchorId) ? null : s.selectionAnchorId,
        }));
        const ts = now();
        const ops: PushOp[] = [
          ...Array.from(deletedIds, (id) => makeDeleteOp('task', id, ts)),
          ...orphanComments.map((c) => makeDeleteOp('comment', c.id, ts)),
        ];
        for (const t of deletedTasks) {
          for (const tagId of t.tagIds ?? []) {
            ops.push(makeDeleteOp('task_tag', `${t.id}:${tagId}`, ts));
          }
        }
        outbox.enqueueMany(ops);
        schedulePushSafely();
      },

      // 批量路径（镜像 importPayload）：一次 set() 更新全部逾期任务 + makeUpsertOp 收集后
      // enqueueMany 一次入队 + 结尾单次 schedulePush。不能逐条走 updateTask —— 每条都
      // enqueue（load+全量扫描+save）+ 一次 set()（Today selector 全表重算），清理 200-300 条
      // 逾期（本功能的目标场景）就是 O(N²) 写放大、主线程冻结数秒。
      // 语义与逐条路径完全一致：每任务应用 rescheduleToDate 的 patch（四列日期原子组单 op，
      // 维持 date-shape CHECK）+ 统一 bump updatedAt（LWW 安全，见 guardrails A 节
      // "所有改 task 字段的本地写路径都必须 bump updatedAt"）。
      rescheduleOverdue: (targetDate) => {
        const overdue = selectTodayOverdue(get());
        if (overdue.length === 0) return 0;
        const stamp = now();
        const patches = new Map(overdue.map((t) => [t.id, rescheduleToDate(t, targetDate)]));
        set((s) => ({
          tasks: s.tasks.map((t) => {
            const patch = patches.get(t.id);
            return patch ? { ...t, ...patch, updatedAt: stamp } : t;
          }),
        }));
        outbox.enqueueMany(
          overdue.map((t) => makeUpsertOp('task', t.id, taskToSyncFields(patches.get(t.id)!))),
        );
        schedulePushSafely();
        return overdue.length;
      },

      setQuickCaptureOpen: (v) => set({ quickCaptureOpen: v }),
      setSearchOpen: (v) => set({ searchOpen: v }),
      setHelpOpen: (v) => set({ helpOpen: v }),
      setCommandPaletteOpen: (v) => set({ commandPaletteOpen: v }),
      toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setListDisplay: (patch) => set((s) => ({ listDisplay: { ...s.listDisplay, ...patch } })),

      // ---------- 同步引擎接口 ----------
      _applyRemote: (changes) => {
        // 跨窗口合并的删除证据（sync/remote-tombstones）：服务器 tombstone 应用后没有
        // outbox 痕迹，且共享 pull cursor 不会重发——不登记的话，另一窗口的陈旧快照
        // 会在跨窗口并集时把已删行无证据地并回来（永久僵尸行）。task_tag 不登记
        // （复合关系，不是快照实体行）。
        const remoteTombstoned: string[] = [];
        for (const list of [
          changes.projects,
          changes.sections,
          changes.tasks,
          changes.comments,
          changes.tags,
        ]) {
          for (const c of list ?? []) {
            if (c.deletedAt) remoteTombstoned.push(c.row.id);
          }
        }
        recordRemoteTombstones(remoteTombstoned);

        let nextPendingTaskTags = getPendingTaskTags();
        // 同名标签归并（防静默丢失）：远端出现「同名（精确）不同 id」的 active tag 时，
        // 本地那个必然是未上行/被服务端 (user_id,name) 唯一索引拒掉的孤儿——两台设备在
        // 同步间隙内各自 #同名 自动建标签就会撞上（op 被拒后 engine 已按 op_id 清账，
        // 不在这里重链接就永久丢关联）。把本地引用就地归并到远端 id，并为受影响任务重新
        // 入队 task_tag upsert。大小写不同的名字不归并（唯一索引精确匹配，可合法共存）。
        // 排除本批 changes.tags 里出现的 id：服务端已知的行不是孤儿——否则「本批同时
        // 收到改名后的新 tag X 与旧 tag Y 改名」时，检测用的是归并前快照（Y 还叫旧名），
        // 会把刚被服务端确认改名的 Y 误判成孤儿，错误地把 Y 的任务重映射到 X 并删掉 Y。
        const tagRemap = new Map<string, string>(); // 本地孤儿 id → 远端 id
        if (changes.tags?.length) {
          const localTags = get().tags;
          const remoteTagIds = new Set(changes.tags.map((c) => c.row.id));
          for (const { row, deletedAt } of changes.tags) {
            if (deletedAt) continue;
            const dup = localTags.find(
              (t) => t.id !== row.id && t.name === row.name && !remoteTagIds.has(t.id),
            );
            if (dup) tagRemap.set(dup.id, row.id);
          }
        }
        const relinkOps: PushOp[] = [];
        if (tagRemap.size > 0) {
          const ts = now();
          for (const t of get().tasks) {
            for (const [fromId, toId] of tagRemap) {
              if ((t.tagIds ?? []).includes(fromId)) {
                relinkOps.push({
                  op_id: uuidv7(),
                  entity_kind: 'task_tag',
                  entity_id: `${t.id}:${toId}`,
                  op_kind: 'upsert',
                  base_row_version: null,
                  client_ts: ts,
                  fields: {},
                });
              }
            }
          }
        }

        set((s) => {
          const projects = mergeList(s.projects, changes.projects);
          const sections = mergeList(s.sections, changes.sections);
          // tagIds 不属于 task 表：syncRowToTask 不返回 tagIds 键（与 mobile 对齐），
          // mergeList 的 {...prev,...row} 天然保留本地 tagIds；新 task（prev 不存在）缺该字段，
          // 读取处全部 `?? []` 兜底，由 task_tag 路径补齐。不要恢复旧的「快照 + 还原 map」
          // 方案——它在每次 pull（含空响应）都克隆整份 tasks（全部新引用）→ useShallow
          // 全判变 → 全列重渲染。引用稳定性由 store.applyRemote.test 钉住。
          let tasks = mergeList(s.tasks, changes.tasks) as Task[];
          let tags = mergeList(s.tags, changes.tags);
          const comments = mergeList(s.comments, changes.comments);

          // 同名标签归并应用：丢本地孤儿行，把任务 tagIds 重映射到远端 id（去重）。
          // tagRemap 为空（常态）时零额外分配，引用稳定性不受影响。
          if (tagRemap.size > 0) {
            tags = tags.filter((t) => !tagRemap.has(t.id));
            tasks = tasks.map((t) => {
              const ids = t.tagIds ?? [];
              if (!ids.some((x) => tagRemap.has(x))) return t;
              return { ...t, tagIds: Array.from(new Set(ids.map((x) => tagRemap.get(x) ?? x))) };
            });
          }

          // task_tag 行 → 更新对应 task 的 tagIds。本批 + 之前缓冲（task 未到）的增量一起处理；
          // buffered 的 row_version 更旧，放前面先应用，保持时间顺序。仍找不到 task 的再缓冲。
          const deltas = [...nextPendingTaskTags, ...(changes.taskTags ?? [])];
          nextPendingTaskTags = [];
          if (deltas.length > 0) {
            const present = new Set(tasks.map((t) => t.id));
            const byTask = new Map<string, { add: Set<string>; remove: Set<string> }>();
            // 父 task 缺席的缓冲按关系键去重（R41 / SY-M1）：
            //   - 删除型 delta（deletedAt 非空）**不缓冲**——父行不存在时它是永久 no-op。
            //     删带标签任务的常态路径（task tombstone + task_tag tombstone 同批到达，
            //     mergeList 先删了 task）曾把整组删除 delta 永久写进 pendingTaskTags，
            //     每次 pull 读出→仍不 present→原样写回 = 缓冲无界增长（还反过来喂
            //     Android CursorWindow 单值超限）。
            //   - 且要顺带覆盖早先缓冲的同关系 add：pull 行是**状态**不是事件，后到的
            //     tombstone 行取代先到的 add 行，否则父行迟到时幽灵 add 会复活已删关联。
            const pendingByRelation = new Map<string, (typeof deltas)[number]>();
            for (const d of deltas) {
              if (!present.has(d.taskId)) {
                const relKey = `${d.taskId} ${d.tagId}`;
                if (d.deletedAt) pendingByRelation.delete(relKey);
                else pendingByRelation.set(relKey, d); // task 还没到，持久化后留到下次 apply
                continue;
              }
              if (!byTask.has(d.taskId))
                byTask.set(d.taskId, { add: new Set(), remove: new Set() });
              const bucket = byTask.get(d.taskId)!;
              if (d.deletedAt) bucket.remove.add(d.tagId);
              else bucket.add.add(d.tagId);
            }
            nextPendingTaskTags = [...pendingByRelation.values()];
            if (byTask.size > 0) {
              tasks = tasks.map((t) => {
                const bucket = byTask.get(t.id);
                if (!bucket) return t;
                const current = new Set(t.tagIds ?? []);
                for (const tagId of bucket.remove) current.delete(tagId);
                for (const tagId of bucket.add) current.add(tagId);
                return { ...t, tagIds: Array.from(current) };
              });
            }
          }

          return { projects, sections, tasks, tags, comments };
        });
        // 必须先持久化 unresolved 关系，engine 才能推进 cursor。否则二者之间崩溃后，
        // 已返回的 task_tag 不会重发，关联会永久丢失。
        setPendingTaskTags(nextPendingTaskTags);

        // 归并产生的重链接 op 在 set 之后一次性入队（enqueue 不得在 set 回调里做副作用）。
        // 收敛且不循环：push 落地后 pull 回来的 task_tag 行是幂等 add，且 dup 已被移除，
        // 不会再次触发归并。
        if (relinkOps.length > 0) {
          outbox.enqueueMany(relinkOps);
          schedulePushSafely();
        }
      },

      _hasAnyData: () => {
        const s = get();
        return s.projects.length > 0 || s.tasks.length > 0;
      },

      _setOwner: (userId) => set({ ownerUserId: userId }),

      bumpDataEpoch: () => set((s) => ({ dataEpoch: s.dataEpoch + 1 })),

      clearLocalData: (newOwnerId) => {
        // 切换用户：清空本地实体 + 未推送的 outbox（否则上一个账号的 outbox 会被推到新账号
        // = 跨租户写），归属设为新用户，随后由 engine bootstrap 拉新用户的数据。
        outbox.clear();
        clearPendingTaskTags(); // 不把上一用户残留的 task_tag 增量带进新会话
        clearRemoteTombstones(); // 跨窗口合并证据同属旧账号会话，一并清（纯卫生）
        set({
          projects: [],
          sections: [],
          tasks: [],
          tags: [],
          comments: [],
          ownerUserId: newOwnerId,
          drawerTaskId: null,
          editingTaskId: null,
          activeRowTaskId: null,
          selectedTaskIds: [],
          selectionAnchorId: null,
        });
      },

      importPayload: (p) => {
        // 合并策略：UUIDv7 单一域，重复 id → 跳过（保留本地）。
        // 全部新行同时 enqueue op，导入数据上行同步。
        const s = get();
        let merged = 0;
        const existProj = new Set(s.projects.map((x) => x.id));
        const existSec = new Set(s.sections.map((x) => x.id));
        const existTask = new Set(s.tasks.map((x) => x.id));
        const existTag = new Set(s.tags.map((x) => x.id));
        const existComment = new Set(s.comments.map((x) => x.id));

        const newProjects = (p.projects ?? []).filter((x) => !existProj.has(x.id));
        const newSections = (p.sections ?? []).filter((x) => !existSec.has(x.id));
        const newTasks = (p.tasks ?? []).filter((x) => !existTask.has(x.id));
        const newTags = (p.tags ?? []).filter((x) => !existTag.has(x.id));
        const newComments = (p.comments ?? []).filter((x) => !existComment.has(x.id));

        merged =
          newProjects.length +
          newSections.length +
          newTasks.length +
          newTags.length +
          newComments.length;
        if (merged === 0) return { merged: 0, persisted: true };

        set((cur) => ({
          projects: [...cur.projects, ...newProjects],
          sections: [...cur.sections, ...newSections],
          tasks: [
            ...cur.tasks,
            ...newTasks.map((t) => ({ ...t, recurrence: t.recurrence ?? null })),
          ],
          tags: [...cur.tags, ...newTags],
          comments: [...cur.comments, ...newComments],
        }));

        // 批量入队走 enqueueMany（一次 load + 一次 save）：逐条 enqueue 时每条都全量
        // JSON.parse + 扫描 + stringify，导入几千行是 O(N²) 同步 localStorage 写，主线程
        // 冻结数秒。产出的 op 集合与逐条完全一致，结尾单次 schedulePush（本就 debounce）。
        const ops: PushOp[] = [
          ...newProjects.map((x) => makeUpsertOp('project', x.id, projectToSyncFields(x))),
          ...newSections.map((x) => makeUpsertOp('section', x.id, sectionToSyncFields(x))),
          ...newTasks.map((x) => makeUpsertOp('task', x.id, taskToSyncFields(x))),
          ...newTags.map((x) =>
            makeUpsertOp('tag', x.id, { name: x.name, color: x.color ?? null }),
          ),
          ...newComments.map((x) =>
            makeUpsertOp('comment', x.id, commentToSyncFields({ taskId: x.taskId, body: x.body })),
          ),
        ];
        // 导入任务的标签关联：task 表不含 tagIds，必须单独 enqueue task_tag op，
        // 否则导入的标签只存在本地、永不上行（换设备 / 清数据后丢失）。只对已知 tag 建关联，
        // 避免服务器 ref_not_owned 拒绝（已知 = 本地原有 + 本次导入的 tag）。
        const knownTagIds = new Set<string>([...existTag, ...newTags.map((x) => x.id)]);
        for (const x of newTasks) {
          for (const tagId of x.tagIds ?? []) {
            if (knownTagIds.has(tagId)) ops.push(makeUpsertOp('task_tag', `${x.id}:${tagId}`, {}));
          }
        }
        outbox.enqueueMany(ops);
        schedulePushSafely();
        // 立刻冲刷 persist 尾随窗口（否则要 150ms 后才知道配额爆），再查两个失败标记：
        // iOS Safari ~5MB 上限下大 JSON 导入会 QuotaExceeded ——「合并了 N 条」但一刷新
        // 全没。persisted=false 让 SettingsView 弹错误提示（分批导入），不弹假成功。
        flushPersistWrites();
        const persisted = !outbox.hasPersistFailure() && !hasPersistWriteFailure();
        return { merged, persisted };
      },
    }),
    {
      name: 'doneline-store-v1',
      version: 2,
      // 尾随合并写（150ms）：默认 storage 在每次 setState（含纯 UI 动作）都全量 stringify
      // 整个实体集写 localStorage；见 lib/persist-storage.ts。pagehide/隐藏时同步 flush。
      // mergeOnForeignWrite（跨窗口 RMW 对称性，guardrail A）：桌面壳 main + quickbar
      // 并发 flush 时，后写的一方先把盘上（对方刚写的）快照 LWW 并进来再落盘，
      // 不再整包覆盖；单窗口下盘上值恒等于自己上次写的，零额外开销。
      storage: coalescedLocalStorage<PersistedShape>(undefined, {
        mergeOnForeignWrite: (pending, disk) => {
          const p = pending.state;
          const d = disk?.state;
          // 版本不一致（旧版本页面残留）→ 不并集，migrate 在 boot 路径负责；
          // 账号不同（切换中）→ 绝不跨租户并集，保持现状 LWW 整包写（后切换者赢，
          // 对方窗口会经 storage 事件整包采纳盘上，实体永不跨 owner 混合）。
          if (!d || pending.version !== disk.version) return pending;
          if (p.ownerUserId !== d.ownerUserId) return pending;
          // 数据代际（guest discard）：高代际赢。盘上更高 → 本窗口快照作废（回写盘上原值，
          // 随后 storage 事件让本窗口整包 rehydrate 拉平）；本窗口更高 → 覆盖盘上旧代际。
          // 缺键（旧 payload）按 0 兜底。相等 → 走既有并集。
          if ((d.dataEpoch ?? 0) > (p.dataEpoch ?? 0)) return { ...pending, state: { ...d } };
          if ((p.dataEpoch ?? 0) > (d.dataEpoch ?? 0)) return pending;
          const localSlice = toEntitiesSlice(p);
          const merged = mergeSnapshots(
            localSlice,
            toEntitiesSlice(d),
            gatherCrossWindowEvidence(),
          );
          if (merged === localSlice) return pending;
          return { ...pending, state: { ...p, ...merged } };
        },
      }),
      partialize: (s) => ({
        projects: s.projects,
        sections: s.sections,
        tasks: s.tasks,
        tags: s.tags,
        comments: s.comments,
        ownerUserId: s.ownerUserId,
        // dataEpoch 与 listDisplay 同款：不 bump version，旧 payload 缺键由初值 0 兜底。
        dataEpoch: s.dataEpoch,
        // 设备本地 UI 偏好：一览行内元素显示开关（不 bump version——旧 payload 缺该
        // key 时由 initial state 的默认值兜底，zustand 浅合并天然兼容）。
        listDisplay: s.listDisplay,
      }),
      // 无 migrate 时，version 不匹配会让 persist **整包丢弃** 本地数据（含尚未 push 的离线
      // 编辑）。默认透传保留旧 payload；未来 schema 变更时在这里按 fromVersion 做字段填充/改名，
      // 绝不返回 undefined（= 清空）。见 local-first-guardrails。
      migrate: (persisted) =>
        persisted as Pick<
          State,
          | 'projects'
          | 'sections'
          | 'tasks'
          | 'tags'
          | 'comments'
          | 'ownerUserId'
          | 'dataEpoch'
          | 'listDisplay'
        >,
    },
  ),
);

// ---------- merge helper ----------

function rowUpdatedAt(x: unknown): string | null {
  if (x && typeof x === 'object' && 'updatedAt' in x) {
    const v = (x as { updatedAt?: unknown }).updatedAt;
    return typeof v === 'string' ? v : null;
  }
  return null;
}

function mergeList<T extends { id: string }>(
  current: T[],
  incoming?: Array<{ row: T; deletedAt: string | null }>,
): T[] {
  if (!incoming || incoming.length === 0) return current;
  const byId = new Map(current.map((x) => [x.id, x]));
  for (const { row, deletedAt } of incoming) {
    if (deletedAt) {
      // Tombstones are authoritative regardless of timestamps.
      byId.delete(row.id);
      continue;
    }
    const prev = byId.get(row.id);
    if (!prev) {
      byId.set(row.id, row);
      continue;
    }
    // Field-level LWW guard. A pull can carry a snapshot OLDER than a change the
    // user just made locally (e.g. an optimistic checkbox toggle whose push
    // hasn't landed yet, or a realtime pulse fired by an unrelated change). A
    // blind `{...prev, ...row}` would revert that change — the classic
    // "tick the checkbox and it springs back" / unstable-sync bug. When both
    // sides carry an `updatedAt`, drop strictly-older remote rows; otherwise
    // fall back to remote-wins merge (entities without `updatedAt`).
    const prevTs = rowUpdatedAt(prev);
    const rowTs = rowUpdatedAt(row);
    // offset-aware 时刻比较（不能裸字符串：client_ts 允许带 +09:00 偏移，字符串序会反转
    // 真实时刻；见 local-first-guardrails A + @doneline/sync isNewer，与服务端 LWW 同一套）。
    // 本地严格更新于远端 → 丢弃旧远端，避免「勾选又弹回」类回滚。
    if (prevTs && rowTs && isNewer(prevTs, rowTs)) continue;
    byId.set(row.id, { ...prev, ...row } as T);
  }
  return Array.from(byId.values());
}

// ---------- selectors (pure) ----------

// 漂浮重复任务：没有任何显式日期，只靠 recurrence 漂浮（如"无日期 + 每周二"）。
// 这类任务在 今日 / 未来 视图里要按 recurrence 投射到具体日期上出现。
export const isFloatingRecurring = (t: Task): t is Task & { recurrence: Recurrence } =>
  !!t.recurrence && !t.dueDate && !t.dueAt && !t.startDate && !t.endDate;

// 返回漂浮重复任务在未来 7 天窗口内首个发生的日期（含今天）；不发生则 null。
// 视图据此把这类无 dueDate 的任务放进对应的日期分组。
export const floatingRecurrenceDate = (t: Task): string | null => {
  if (!isFloatingRecurring(t)) return null;
  const anchor = todayStr();
  for (const d of next7Days()) {
    if (recurrenceOccursOn(t.recurrence, d, anchor)) return d;
  }
  return null;
};

const taskCoversToday = (t: Task) =>
  isToday(t.dueDate) ||
  inRangeToday(t.startDate, t.endDate) ||
  (t.dueAt ? isToday(t.dueAt.slice(0, 10)) : false) ||
  (isFloatingRecurring(t) && recurrenceOccursOn(t.recurrence, todayStr(), todayStr()));

// 全局展示排序 = @doneline/shared 的 compareTasks：priority 降序优先（P1→P4），
// 同优先级内 sortKey 字典序，createdAt + id 打破并列。web/mobile 共用一份口径。

// 一个开放任务是否"逾期"：覆盖全部 4 种日期形态，而不只是 dueDate。
// 修复前只看 dueDate —— 过期的「时点任务」(dueAt) 和「已结束的日期区间」(endDate)
// 既不算今天也不算逾期、也不进未来，会从所有视图里彻底消失（只能进项目页找）。
export const taskIsOverdue = (t: Task): boolean =>
  isOverdue(t.dueDate) ||
  (t.dueAt ? isOverdue(t.dueAt.slice(0, 10)) : false) ||
  (t.endDate ? isOverdue(t.endDate) : false);

export const selectTodayActive = (s: State) =>
  s.tasks.filter((t) => t.status === 0 && !t.parentTaskId && taskCoversToday(t)).sort(compareTasks);

export const selectTodayOverdue = (s: State) =>
  s.tasks.filter((t) => t.status === 0 && !t.parentTaskId && taskIsOverdue(t)).sort(compareTasks);

export const selectTodayCompleted = (s: State) =>
  s.tasks
    .filter(
      (t) =>
        t.status === 1 &&
        !t.parentTaskId &&
        // completedAt 是 timestamptz：必须按**本地日历日**判「今天完成」（isToday 对瞬时做本地日比较）。
        // 曾用 UTC-slice 对比本地 todayStr —— UTC+8 用户凌晨 02:43 完成的任务不进「今日已完成」
        // （UTC 日还是昨天）。CI 跑 UTC 时两者恰好相等，被 vitest 钉 TZ=Asia/Shanghai 暴露。
        isToday(t.completedAt),
    )
    .sort(compareTasks);

export const selectInbox = (s: State) =>
  s.tasks
    .filter((t) => t.projectId === null && t.status === 0 && !t.parentTaskId)
    .sort(compareTasks);

// 任务在「未来 7 天」窗口里落在哪一天 —— 选择器与 UpcomingView 的分组共用这一处
// 真相。覆盖全部 4 种日期形态（修复前只认 dueDate，dueAt/区间任务永远进不了未来视图）：
//   - dueDate：当天
//   - dueAt：取其日期部分
//   - 日期区间：整段已过 → null；否则落在「开始日」或「今天」（区间跨越今天时）
//   - 漂浮重复：窗口内首个发生日
export const upcomingDay = (t: Task): string | null => {
  if (t.dueDate) return t.dueDate;
  if (t.dueAt) return t.dueAt.slice(0, 10);
  if (t.startDate && t.endDate) {
    const today = todayStr();
    if (t.endDate < today) return null;
    return t.startDate >= today ? t.startDate : today;
  }
  return floatingRecurrenceDate(t);
};

const coversUpcoming = (t: Task, days: Set<string>) => {
  if (t.status !== 0 || t.parentTaskId) return false;
  const day = upcomingDay(t);
  // days 只含今天起 7 天，天然排除逾期；window 命中即收录
  return day !== null && days.has(day);
};

export const selectUpcomingCount = (s: State) => {
  const days = new Set(next7Days());
  return s.tasks.filter((t) => coversUpcoming(t, days)).length;
};

export const selectByProject = (s: State, projectId: string) =>
  s.tasks.filter((t) => t.projectId === projectId);

// 标签过滤视图：含该 tag 的全部任务（含已完成，视图侧自行分组），priority-first 排序。
// 顶层任务与子任务都收——打了标签的子任务也应出现在标签视图（Todoist 语义）。
export const selectByTag = (s: State, tagId: string) =>
  s.tasks.filter((t) => (t.tagIds ?? []).includes(tagId)).sort(compareTasks);

/**
 * Open (status === 0) task count per tag. Sidebar 标签区的数字徽标，
 * 一次全表扫描出 Map，避免逐行 filter 的 O(N×tags)。
 */
export const selectOpenCountByTag = (s: State): Record<string, number> => {
  const counts: Record<string, number> = {};
  for (const task of s.tasks) {
    if (task.status !== 0) continue;
    for (const id of task.tagIds ?? []) {
      counts[id] = (counts[id] ?? 0) + 1;
    }
  }
  return counts;
};

/**
 * Open (status === 0) task count per project. Used by the sidebar to render a
 * Todoist-style numeric badge next to each project name.
 */
export const selectOpenCountByProject = (s: State): Record<string, number> => {
  const counts: Record<string, number> = {};
  for (const task of s.tasks) {
    if (task.status !== 0) continue;
    if (task.parentTaskId) continue;
    if (!task.projectId) continue;
    counts[task.projectId] = (counts[task.projectId] ?? 0) + 1;
  }
  return counts;
};

// ---- 每行 selector 的引用记忆化（perf B1）----
// zustand v5 在每次 set() 时同步跑全部订阅者的 selector；每行 O(N) 全表扫会把一次
// setState 放大成 O(可见行数 × 任务总数)（Today 40 行 × 2000 任务 ≈ 16 万次迭代/键）。
// 以 tasks/comments **数组引用**为 key 一次性建 Map：首个订阅者付 O(N)，其余 O(1)。
// 前提（护栏 E 已钉）：store 全程不可变更新 + _applyRemote 保持未变行引用稳定，
// 因此「同一数组引用 ⇒ 内容未变」恒成立，WeakMap 命中即安全；返回稳定引用还让
// useShallow 退化为引用比较。缓存构建期的局部 push/累加不违反不可变规则（只在
// 本函数内可见，写入 Map 后不再修改）。
// ⚠️ 返回值是**共享缓存引用**（含 EMPTY_*）——调用方只读，禁止就地 sort/reverse/
// 改字段（会跨订阅者串改缓存）；需要变形先自行浅拷贝。
const EMPTY_PROGRESS: { done: number; total: number } = { done: 0, total: 0 };
const EMPTY_CHILDREN: Task[] = [];

const childrenCache = new WeakMap<Task[], Map<string, Task[]>>();
function childrenMap(tasks: Task[]): Map<string, Task[]> {
  let m = childrenCache.get(tasks);
  if (!m) {
    m = new Map();
    for (const t of tasks) {
      if (!t.parentTaskId) continue;
      const arr = m.get(t.parentTaskId);
      if (arr) arr.push(t);
      else m.set(t.parentTaskId, [t]);
    }
    for (const arr of m.values()) arr.sort(compareTasks);
    childrenCache.set(tasks, m);
  }
  return m;
}

export const selectChildrenOf = (s: State, parentId: string) =>
  childrenMap(s.tasks).get(parentId) ?? EMPTY_CHILDREN;

const progressCache = new WeakMap<Task[], Map<string, { done: number; total: number }>>();
export const selectSubtaskProgress = (
  s: State,
  parentId: string,
): { done: number; total: number } => {
  let m = progressCache.get(s.tasks);
  if (!m) {
    m = new Map();
    for (const t of s.tasks) {
      if (!t.parentTaskId) continue;
      let p = m.get(t.parentTaskId);
      if (!p) {
        p = { done: 0, total: 0 };
        m.set(t.parentTaskId, p);
      }
      p.total += 1;
      if (t.status === 1) p.done += 1;
    }
    progressCache.set(s.tasks, m);
  }
  return m.get(parentId) ?? EMPTY_PROGRESS;
};

export const selectUpcoming = (s: State) => {
  const set = new Set(next7Days());
  // 排序后再按天分桶（filter 保序）→ 每个日期桶内也是 priority-first。
  return s.tasks.filter((t) => coversUpcoming(t, set)).sort(compareTasks);
};

export const selectCompletedRecent = (s: State, days = 7) => {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - days);
  return s.tasks.filter(
    (t) => t.status === 1 && t.completedAt && new Date(t.completedAt) >= cutoff,
  );
};

// Comment count for a task. Returns a primitive so the selector is re-render-safe
// (a row only re-renders when its own count changes). 同款引用记忆化（perf B1）。
const commentCountCache = new WeakMap<Comment[], Map<string, number>>();
export const selectCommentCount = (s: State, taskId: string): number => {
  let m = commentCountCache.get(s.comments);
  if (!m) {
    m = new Map();
    for (const c of s.comments) m.set(c.taskId, (m.get(c.taskId) ?? 0) + 1);
    commentCountCache.set(s.comments, m);
  }
  return m.get(taskId) ?? 0;
};

// Project membership of the current multi-selection — drives the BulkActionBar
// project control so the user can tell whether they're about to move a uniform
// set or scatter a mixed one.
export type SelectedProjectScope =
  | { kind: 'none' }
  | { kind: 'uniform'; projectId: string | null }
  | { kind: 'mixed' };

export const selectSelectedProjectScope = (s: State): SelectedProjectScope => {
  if (s.selectedTaskIds.length === 0) return { kind: 'none' };
  const selected = new Set(s.selectedTaskIds);
  let projectId: string | null | undefined;
  for (const t of s.tasks) {
    if (!selected.has(t.id)) continue;
    if (projectId === undefined) projectId = t.projectId;
    else if (projectId !== t.projectId) return { kind: 'mixed' };
  }
  return projectId === undefined ? { kind: 'none' } : { kind: 'uniform', projectId };
};

// Re-export priority type for convenience
export type { Priority };
